import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import {
  CreateElectionDto,
  ElectionResponseDto,
  UpdateElectionDto
} from '@thro-clients/polyas-connector-api';
import { User } from '@thro-types/users';

import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useCreateElectionMutation, useGetElectionByIdForUpdate } from 'api/hooks/polyas/polyas';
import { AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import usePageHeading from 'utils/hooks/usePageHeading';

import { CreateElectionSkeleton } from '../components/election-skeletons';
import { electionValidationSchema, getElectionInitialValues } from './validation';

interface Props {
  currentUser: User;
}

export default function ElectionEditPage({ currentUser }: Props) {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  usePageHeading({
    heading: !id
      ? t(translations.pages.polyas.edit.createHeader())
      : t(translations.pages.polyas.edit.header()),
    headingProps: { back: location.state?.back ?? '/election/admin' }
  });

  const { data, isLoading } = useGetElectionByIdForUpdate(id);

  useEffect(() => {
    if (id && data && data.status !== 'future') {
      navigate('/election/admin', { replace: true });
    }
  }, [id, data, navigate]);

  if (id && isLoading) {
    return <CreateElectionSkeleton />;
  }

  if (id && data && data.status !== 'future') {
    return null;
  }

  return <Content data={data} id={id} />;
}

type ElectionFormValues = {
  title: string;
  description?: string;
  electionStartDate: Date | null;
  electionEndDate: Date | null;
  visibleStartDate: Date | null;
  visibleEndDate: Date | null;
  polyasProjectId: string;
  polyasSharedSecret: string;
};

interface ContentProps {
  data?: ElectionResponseDto;
  id?: string;
}

function Content({ data, id }: ContentProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { create, update } = useCreateElectionMutation();

  const {
    watch,
    handleSubmit,
    formState: { errors },
    control,
    register
  } = useForm<ElectionFormValues>({
    defaultValues: getElectionInitialValues(data),
    resolver: zodResolver(electionValidationSchema())
  });

  const handleSaveClick = async (values: ElectionFormValues) => {
    try {
      let response: ElectionResponseDto | null = null;
      if (!!id) {
        const toUpdate: UpdateElectionDto = {
          title: values.title,
          description: values.description,
          visibleStartDate: values.visibleStartDate?.toISOString() ?? undefined,
          visibleEndDate: values.visibleEndDate?.toISOString() ?? undefined,
          electionStartDate: values.electionStartDate?.toISOString() ?? undefined,
          electionEndDate: values.electionEndDate?.toISOString() ?? undefined,
          isManuallyEnded: false,
          polyasProjectId: values.polyasProjectId,
          polyasSharedSecret: values.polyasSharedSecret ?? undefined
        };
        response = await update.mutateAsync({ id: id, data: toUpdate });
        dispatch(AlertActions.success(t(translations.pages.polyas.edit.editSuccess())));
      } else {
        if (
          !values.visibleEndDate ||
          !values.visibleStartDate ||
          !values.electionStartDate ||
          !values.electionEndDate
        ) {
          // !HANDLE ERROR HERE
          // TODO Normally we wont come here because this is checked ealier.
          return;
        }

        const toCreate: CreateElectionDto = {
          title: values.title,
          description: values.description,
          visibleStartDate: values.visibleStartDate?.toISOString(),
          visibleEndDate: values.visibleEndDate?.toISOString(),
          electionStartDate: values.electionStartDate?.toISOString(),
          electionEndDate: values.electionEndDate?.toISOString(),
          polyasProjectId: values.polyasProjectId,
          polyasSharedSecret: values.polyasSharedSecret
        };

        response = await create.mutateAsync({ data: toCreate });
        dispatch(AlertActions.success(t(translations.pages.polyas.edit.createSuccess())));
      }
      navigate(`/election/admin/${response.id}`, { replace: true });
    } catch {
      if (!id) {
        dispatch(AlertActions.error(t(translations.pages.polyas.edit.createFailed())));
      } else {
        dispatch(AlertActions.error(t(translations.pages.polyas.edit.editFailed())));
      }
    }
  };
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* {create.isError && (
        <Alert severity="error">Fehler beim Erstellen der Wahl: {create.error?.message}</Alert>
      )} */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t(translations.pages.polyas.header())}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t(translations.pages.polyas.info())}
          </Typography>
          <form onSubmit={handleSubmit(values => handleSaveClick(values))}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
              <TextField
                {...register('title')}
                label={t(translations.pages.polyas.fields.title())}
                id="title"
                error={!!errors.title}
                helperText={errors.title?.message}
                placeholder="z.B. Fakultätsratswahl 2026"
                required
                fullWidth
              />
              <TextField
                {...register('description')}
                label={t(translations.pages.polyas.fields.description())}
                id="description"
                error={!!errors.description}
                helperText={errors.description?.message}
                placeholder="Optionale Beschreibung der Wahl..."
                multiline
                rows={3}
                fullWidth
              />

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 2
                }}
              >
                <Controller
                  name="electionStartDate"
                  control={control}
                  render={({ field }) => (
                    <DateTimePicker
                      {...field}
                      renderInput={props => (
                        <TextField
                          {...props}
                          required
                          fullWidth
                          variant="outlined"
                          label={t(translations.pages.polyas.fields.electionStartDate())}
                          error={!!errors.electionStartDate}
                          helperText={errors.electionStartDate?.message}
                        />
                      )}
                      mask="__.__.____ __:__"
                      inputFormat="dd.MM.yyyy HH:mm"
                      ampm={false}
                      ampmInClock={false}
                      minutesStep={15}
                      minDateTime={watch('visibleStartDate') ?? new Date()}
                      onChange={value => field.onChange(value)}
                    />
                  )}
                />
                <Controller
                  name="electionEndDate"
                  control={control}
                  render={({ field }) => (
                    <DateTimePicker
                      {...field}
                      renderInput={props => (
                        <TextField
                          {...props}
                          required
                          fullWidth
                          variant="outlined"
                          label={t(translations.pages.polyas.fields.electionEndDate())}
                          error={!!errors.electionEndDate}
                          helperText={errors.electionEndDate?.message}
                        />
                      )}
                      mask="__.__.____ __:__"
                      inputFormat="dd.MM.yyyy HH:mm"
                      ampm={false}
                      ampmInClock={false}
                      minutesStep={15}
                      minDateTime={watch('electionStartDate') ?? new Date()}
                      onChange={value => field.onChange(value)}
                    />
                  )}
                />
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 2
                }}
              >
                <Controller
                  name="visibleStartDate"
                  control={control}
                  render={({ field }) => (
                    <DateTimePicker
                      {...field}
                      renderInput={props => (
                        <TextField
                          {...props}
                          required
                          fullWidth
                          variant="outlined"
                          label={t(translations.pages.polyas.fields.visibleStartDate())}
                          error={!!errors.visibleStartDate}
                          helperText={errors.visibleStartDate?.message}
                        />
                      )}
                      mask="__.__.____ __:__"
                      inputFormat="dd.MM.yyyy HH:mm"
                      ampm={false}
                      ampmInClock={false}
                      minutesStep={15}
                      minDateTime={new Date()}
                      onChange={value => field.onChange(value)}
                    />
                  )}
                />
                <Controller
                  name="visibleEndDate"
                  control={control}
                  render={({ field }) => (
                    <DateTimePicker
                      {...field}
                      renderInput={props => (
                        <TextField
                          {...props}
                          required
                          fullWidth
                          variant="outlined"
                          label={t(translations.pages.polyas.fields.visibleEndDate())}
                          error={!!errors.visibleEndDate}
                          helperText={errors.visibleEndDate?.message}
                        />
                      )}
                      mask="__.__.____ __:__"
                      inputFormat="dd.MM.yyyy HH:mm"
                      ampm={false}
                      ampmInClock={false}
                      minutesStep={15}
                      minDateTime={watch('electionEndDate') ?? new Date()}
                      onChange={value => field.onChange(value)}
                    />
                  )}
                />
              </Box>

              <TextField
                {...register('polyasProjectId')}
                id="polyasProjectId"
                label={t(translations.pages.polyas.fields.polyasProjectId())}
                error={!!errors.polyasProjectId}
                helperText={errors.polyasProjectId?.message}
                placeholder="Eindeutiger Polyas Project-Identifier"
                required
                fullWidth
              />

              <TextField
                id="polyasSharedSecret"
                type="password"
                label={t(translations.pages.polyas.fields.polyasSharedSecret())}
                // disabled={!!id}
                {...register('polyasSharedSecret')}
                error={!!errors.polyasSharedSecret}
                helperText={errors.polyasSharedSecret?.message}
                placeholder="Geheimer Schlüssel für die Wahl"
                required
                fullWidth
              />

              <Divider sx={{ my: 1 }} />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button variant="outlined" href="/election/admin">
                  {t(translations.pages.polyas.edit.cancel())}
                </Button>
                <Button type="submit" variant="contained" disabled={create.isPending}>
                  {!id
                    ? create.isPending
                      ? t(translations.pages.polyas.edit.currentlyCreatingButton())
                      : t(translations.pages.polyas.edit.createButton())
                    : update.isPending
                    ? t(translations.pages.polyas.edit.currentlyEditingButton())
                    : t(translations.pages.polyas.edit.editButton())}
                </Button>
              </Box>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
